import { IconSparkles } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { type GptFeedScope } from "../../domains/gpt/hooks/useEnsureGptConversation";
import { Avatar } from "../ui/Avatar";
import { PromoCard } from "../ui/PromoCard";

// The end of an exhausted Practice or Topic feed: the reader has scrolled
// through everything the library holds, so the next step is JuisciGPT searching
// PubMed inside the same feed. Same hand-off as "Ask JuisciGPT" on search: the
// landing auto-submits `initialQuestion` and mints the conversation with
// `feedScope`, which the agent resolves to the feed's PubMed query every turn.
// A PromoCard, the card both feed pages already stack under the list for the
// recommended room. Rendered right after the divided list, inside the padded
// column, so it takes the column's width and leaves no rule under the last
// article; the caller shows it only once the list is complete, never on an
// empty one.
//
// `narrowing` is the rail chip a practice feed was filtered on, as the rail
// labels it, or null for the whole feed. With a chip the card and the question
// name the chip: that is what the reader just ran out of, and what the agent's
// scope is ANDed with server-side.
export function FeedEndAskGpt({
  feedScope,
  title,
  narrowing,
}: {
  feedScope: GptFeedScope;
  title: string;
  narrowing: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const initialQuestion =
    narrowing === null
      ? t("Find me more recent publications on {{title}}", { title })
      : t("Find me more recent publications on {{narrowing}} in {{title}}", {
          narrowing,
          title,
        });
  return (
    <PromoCard
      leading={
        <Avatar
          variant="icon"
          size="md"
          icon={<IconSparkles size={16} stroke={1.5} />}
          badge={null}
          // The accent of every JuisciGPT entry point ("Ask JuisciGPT" on
          // search), so the card reads as the agent's and not as one more room.
          className="bg-ds-lime-400 text-ds-lime-950"
        />
      }
      title={t("The latest, live from PubMed")}
      description={t(
        "Our research agent finds what came out since on {{title}}.",
        { title: narrowing === null ? title : narrowing }
      )}
      onClick={() => {
        history.push({
          pathname: "/gpt/chat/new",
          state: { initialQuestion, feedScope },
        });
      }}
    />
  );
}
